import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EmptyState, PageHeader } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function AdminNotifications() {
  const { tenantId } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ key: "", name: "", subject: "", body: "", channels: ["in_app", "email"] });

  const load = () => {
    if (!tenantId) return;
    api.get(`/tenants/${tenantId}/notification-templates`).then(({ data }) => setRows(data)).catch((e) => toast.error(errMsg(e)));
  };
  useEffect(load, [tenantId]); // eslint-disable-line

  return (
    <div className="space-y-10">
      <PageHeader
        testid="admin-notifications-header"
        eyebrow="Administration"
        title="Notification manager"
        description="Templates, channels and timing are configurable. Restricted items are never expanded inside email bodies."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-template-button" className="h-9 rounded-none text-[10px] uppercase tracking-[0.18em]">New template</Button>
            </DialogTrigger>
            <DialogContent className="rounded-none">
              <DialogHeader><DialogTitle className="font-display text-2xl">Notification template</DialogTitle></DialogHeader>
              <div className="space-y-4">
                {[["key", "Key"], ["name", "Name"], ["subject", "Subject"]].map(([k, l]) => (
                  <div key={k} className="space-y-2">
                    <Label className="label-caps">{l}</Label>
                    <Input data-testid={`template-${k}-input`} className="rounded-none" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
                  </div>
                ))}
                <div className="space-y-2">
                  <Label className="label-caps">Body</Label>
                  <Textarea data-testid="template-body-input" rows={4} className="rounded-none text-xs" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
                </div>
                <Button
                  data-testid="create-template-submit"
                  className="w-full rounded-none"
                  disabled={!form.key || !form.subject}
                  onClick={async () => {
                    try {
                      await api.post(`/tenants/${tenantId}/notification-templates`, form);
                      toast.success("Template saved");
                      setOpen(false);
                      setForm({ key: "", name: "", subject: "", body: "", channels: ["in_app", "email"] });
                      load();
                    } catch (e) {
                      toast.error(errMsg(e));
                    }
                  }}
                >
                  Save template
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        }
      />
      <div className="panel divide-y divide-border" data-testid="templates-list">
        {rows.map((t) => (
          <div key={t.id} className="px-5 py-4" data-testid={`template-${t.key}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm">{t.name || t.key}</p>
              <div className="flex gap-2">
                {(t.channels || []).map((c) => (
                  <span key={c} className="border border-border px-2 py-0.5 font-mono text-[10px] uppercase">{c}</span>
                ))}
              </div>
            </div>
            <p className="label-caps mt-2">{t.subject}</p>
            <p className="mt-1 text-xs text-muted-foreground">{t.body}</p>
          </div>
        ))}
        {rows.length === 0 && <EmptyState testid="templates-empty" title="No templates" message="Create notification templates for this engagement." />}
      </div>
    </div>
  );
}
